/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { STEP_SIZES } from "features/results/components/HistoricalDataFilters"
import _ from "lodash"

import { Cohort, InitialScrsTable } from "./types"
import { assertFiniteColumn } from "./utils"

// Determine number of decimal places based on bounds step size (for display)
// Step 1 => 0 decimals, Step 0.1 => 1 decimal, Step 0.01 => 2 decimals
const CAPE_DECIMALS = STEP_SIZES.CAPE < 1 ? String(STEP_SIZES.CAPE).split(".")[1].length : 0

// Use the specified display decimals for drawdown
const DRAWDOWN_DECIMALS = STEP_SIZES.DISPLAY.DRAWDOWN_DECIMALS

export function makeCapeCohorts(isErnCape: boolean): Cohort[] {
  return [
    {},
    {
      firstStartingYear: 1926,
      cape: { ern: isErnCape },
    },
    {
      cape: { ern: isErnCape, maxValue: 20 },
    },
    {
      cape: { ern: isErnCape, minValue: 20 },
    },
    {
      cape: { ern: isErnCape, minValue: 20 },
      maxAbsDrawdown: 0,
    },
  ]
}

export const DRAWDOWN_COHORTS: Cohort[] = [
  {
    maxAbsDrawdown: 0,
  },
  {
    minAbsDrawdown: 0,
    maxAbsDrawdown: 0.1,
  },
  {
    minAbsDrawdown: 0.1,
    maxAbsDrawdown: 0.2,
  },
  {
    minAbsDrawdown: 0.2,
    maxAbsDrawdown: 0.3,
  },
  {
    minAbsDrawdown: 0.3,
  },
]

// A special "All" cohort that includes all data
export const ALL_COHORT: Cohort = {
  description: "All",
}

/**
 * Filters initial SCRs data based on cohort criteria.
 * Closely matches the server-side _filter_cohorts function by:
 * 1. Converting CAPE values in the cohort to CAEY (earnings yield = 1/CAPE)
 * 2. Using the correct column names for comparison
 *
 * @param dataTable The prepared data table with properly typed columns
 * @param cohort The cohort definition with filtering criteria
 * @returns Filtered InitialScrsTable with all columns
 */
export function filterCohorts(dataTable: InitialScrsTable, cohort: Cohort): InitialScrsTable {
  // Check if dataTable has required data
  if (dataTable.initial_scr.length === 0) {
    // Return empty table structure
    return {
      initial_scr: [],
      year: [],
      month: [],
      caey_ern_1M_lag: [],
      caey_shiller_1M_lag: [],
      real_equity_drawdown_1M_lag: [],
      c1: [],
      sum_of_ct: [],
      sum_of_ctpt: [],
    }
  }

  // Return early with all values for the "All" cohort
  if (
    !cohort.cape &&
    cohort.firstStartingYear === undefined &&
    cohort.lastStartingYear === undefined &&
    cohort.minAbsDrawdown === undefined &&
    cohort.maxAbsDrawdown === undefined
  ) {
    return dataTable
  }

  let caeyMin: number | undefined
  let caeyMax: number | undefined

  if (cohort.cape) {
    if (cohort.cape.maxValue !== undefined && cohort.cape.maxValue > 0) {
      caeyMin = 1 / cohort.cape.maxValue // CAPE max = CAEY min
    }
    if (cohort.cape.minValue !== undefined && cohort.cape.minValue > 0) {
      caeyMax = 1 / cohort.cape.minValue // CAPE min = CAEY max
    }
  }

  // Named rather than picked out inline so the guard below can report which of the
  // two CAEY columns the cohort was actually read from.
  const caeyColumn = cohort.cape?.ern ? "caey_ern_1M_lag" : "caey_shiller_1M_lag"
  const caeyValues = dataTable[caeyColumn]
  const drawdownValues = dataTable.real_equity_drawdown_1M_lag

  let minDrawdown: number | undefined
  let maxDrawdown: number | undefined

  if (cohort.minAbsDrawdown !== undefined) {
    minDrawdown = -cohort.minAbsDrawdown
  }
  if (cohort.maxAbsDrawdown !== undefined) {
    maxDrawdown = -cohort.maxAbsDrawdown
  }

  // AIDEV-NOTE: issue #781. Guarded here rather than inside the per-row predicate
  // because the comparisons below are relational: a null coerces to 0, so it is
  // accepted or rejected by whichever bound happens to straddle zero instead of
  // being recognised as absent. Only the columns this cohort actually compares
  // against are scanned - a column nobody reads cannot be mis-bucketed, and the
  // cost then stays proportional to the filter it protects rather than to the
  // table's full column count.
  if (caeyMin !== undefined || caeyMax !== undefined) {
    assertFiniteColumn(caeyValues, caeyColumn, "filterCohorts")
  }
  if (minDrawdown !== undefined || maxDrawdown !== undefined) {
    assertFiniteColumn(drawdownValues, "real_equity_drawdown_1M_lag", "filterCohorts")
  }

  // Build an array of indices that pass all filters
  const validIndices = Array.from({ length: dataTable.initial_scr.length }, (_, i) => i).filter(
    index => {
      // Year filtering - if firstStartingYear or lastStartingYear is specified
      if (cohort.firstStartingYear !== undefined || cohort.lastStartingYear !== undefined) {
        const year = dataTable.year[index]

        // Check lower bound if firstStartingYear is specified
        if (cohort.firstStartingYear !== undefined && year < cohort.firstStartingYear) {
          return false
        }

        // Check upper bound if lastStartingYear is specified
        if (cohort.lastStartingYear !== undefined && year > cohort.lastStartingYear) {
          return false
        }
      }

      // CAEY filtering (earnings yield = 1/CAPE)
      if (caeyMin !== undefined || caeyMax !== undefined) {
        const caey = caeyValues[index]

        if (
          (caeyMin !== undefined && caey < caeyMin) ||
          (caeyMax !== undefined && caey > caeyMax)
        ) {
          return false
        }
      }

      // Drawdown filtering
      if (minDrawdown !== undefined || maxDrawdown !== undefined) {
        const drawdown = drawdownValues[index]

        if (
          (minDrawdown !== undefined && drawdown > minDrawdown) ||
          (maxDrawdown !== undefined && drawdown < maxDrawdown)
        ) {
          return false
        }
      }

      // All criteria passed
      return true
    }
  )

  // Create a new filtered table with all columns
  const filteredTable: InitialScrsTable = {
    initial_scr: validIndices.map(i => dataTable.initial_scr[i]),
    year: validIndices.map(i => dataTable.year[i]),
    month: validIndices.map(i => dataTable.month[i]),
    caey_ern_1M_lag: validIndices.map(i => dataTable.caey_ern_1M_lag[i]),
    caey_shiller_1M_lag: validIndices.map(i => dataTable.caey_shiller_1M_lag[i]),
    real_equity_drawdown_1M_lag: validIndices.map(i => dataTable.real_equity_drawdown_1M_lag[i]),
    c1: validIndices.map(i => dataTable.c1[i]),
    sum_of_ct: validIndices.map(i => dataTable.sum_of_ct[i]),
    sum_of_ctpt: validIndices.map(i => dataTable.sum_of_ctpt[i]),
  }

  return filteredTable
}

export function describeCohort(cohort: Cohort): string {
  const parts: string[] = []

  // Helper function to round and format values
  const formatNumber = (value: number, decimals: number): string => {
    return value.toFixed(decimals)
  }

  // 1. Starting‑year range
  if (cohort.firstStartingYear != null || cohort.lastStartingYear != null) {
    const from = cohort.firstStartingYear
    const to = cohort.lastStartingYear
    if (from != null && to != null) {
      parts.push(from === to ? `Year = ${from}` : `${from} ≤ Year ≤ ${to}`)
    } else if (from != null) {
      parts.push(`Year ≥ ${from}`)
    } else {
      parts.push(`Year ≤ ${to}`)
    }
  }

  // 2. CAPE filter
  if (cohort.cape) {
    const { minValue, maxValue } = cohort.cape
    if (minValue != null || maxValue != null) {
      const minDisplay = minValue != null ? formatNumber(minValue, CAPE_DECIMALS) : null
      const maxDisplay = maxValue != null ? formatNumber(maxValue, CAPE_DECIMALS) : null

      parts.push(
        minDisplay != null && maxDisplay != null
          ? minDisplay === maxDisplay
            ? `CAPE = ${minDisplay}`
            : `${minDisplay} ≤ CAPE ≤ ${maxDisplay}`
          : minDisplay != null
            ? `CAPE ≥ ${minDisplay}`
            : `CAPE ≤ ${maxDisplay}`
      )
    }
  }

  // 3. Drawdown filter
  if (cohort.minAbsDrawdown != null || cohort.maxAbsDrawdown != null) {
    const lowVal = cohort.minAbsDrawdown ?? 0
    const highVal = cohort.maxAbsDrawdown ?? 0
    // Use the DRAWDOWN_DECIMALS constant to ensure consistent formatting
    const lowStr = `${(lowVal * 100).toFixed(DRAWDOWN_DECIMALS)}%`
    const highStr = `${(highVal * 100).toFixed(DRAWDOWN_DECIMALS)}%`

    parts.push(
      lowVal === highVal
        ? `Drawdown = ${lowStr}`
        : cohort.minAbsDrawdown != null && cohort.maxAbsDrawdown != null
          ? `${lowStr} ≤ Drawdown ≤ ${highStr}`
          : cohort.minAbsDrawdown != null
            ? `Drawdown ≥ ${lowStr}`
            : `Drawdown ≤ ${highStr}`
    )
  }

  if (parts.length === 0) {
    return "No Filters"
  }

  return parts.join("; ")
}
